import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { fetchCampaignContestByID, updateCampaignContest, fetchAllCampaigns } from '../../api/campaigns';
import { fetchCompTeams } from '../../api/userTeams';
import { fetchAllRewards } from '../../api/rewards';
import { getConstraintTypes } from '../../api/constraints';
import { filterTempTeams } from '../../helpers/teamHelpers';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';
import BackButton from '../../components/BackButton';
import ConstraintEditor from '../../components/admin/ConstraintEditor';

const formatRewardLabel = (r) => {
    const parts = [];
    if (r.money) parts.push(`$${r.money}`);
    if (r.numberOfSeries) parts.push(`${r.numberOfSeries} packs`);
    if (r.Card) parts.push(`${r.Card.year} ${r.Card.name}`);
    else if (r.cardID) parts.push(`Card #${r.cardID}`);
    if (r.description) parts.push(r.description);
    return parts.length > 0 ? parts.join(' + ') : `Reward #${r.rewardID}`;
};

const EditCampaignContestPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const { campaignContestID } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [compTeams, setCompTeams] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [constraintTypeDefs, setConstraintTypeDefs] = useState([]);
    const [formData, setFormData] = useState({
        campaignID: '',
        name: '',
        desc: '',
        userTeamID: '',
        rewardID: '',
        numOfGames: '',
        maxRewards: '',
        constraints: [],
        links: [],
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [data, campaignData, teamData, rewardData] = await Promise.all([
                    fetchCampaignContestByID(campaignContestID),
                    fetchAllCampaigns(),
                    fetchCompTeams(),
                    fetchAllRewards(),
                ]);
                getConstraintTypes().then(setConstraintTypeDefs).catch(err => console.error('Failed to fetch constraint types:', err));
                setCampaigns(campaignData);
                setCompTeams(filterTempTeams(teamData));
                setRewards(rewardData);
                setFormData({
                    campaignID: data.campaignID,
                    name: data.name,
                    desc: data.desc,
                    userTeamID: data.userTeamID,
                    rewardID: data.rewardID ?? '',
                    numOfGames: data.numOfGames,
                    maxRewards: data.maxRewards ?? '',
                    constraints: Array.isArray(data.constraints) ? data.constraints : [],
                    links: Array.isArray(data.links) ? data.links : [],
                });
            } catch (err) {
                setError('Error fetching campaign contest');
                toast.error('Error fetching campaign contest: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [campaignContestID]);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLinkChange = (index, field, value) => {
        setFormData(prev => {
            const next = prev.links.map((link, i) =>
                i === index ? { ...link, [field]: value } : link
            );
            return { ...prev, links: next };
        });
    };

    const handleAddLink = () => {
        setFormData(prev => ({
            ...prev,
            links: [...prev.links, { label: '', url: '' }],
        }));
    };

    const handleRemoveLink = index => {
        setFormData(prev => ({
            ...prev,
            links: prev.links.filter((_, i) => i !== index),
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const cleanedLinks = formData.links
                .map(l => ({ label: (l.label || '').trim(), url: (l.url || '').trim() }))
                .filter(l => l.label && l.url);
            await updateCampaignContest(campaignContestID, {
                ...formData,
                rewardID: formData.rewardID ? formData.rewardID : null,
                numOfGames: formData.numOfGames ? parseInt(formData.numOfGames, 10) : 7,
                maxRewards: formData.maxRewards ? parseInt(formData.maxRewards, 10) : null,
                constraints: formData.constraints,
                links: cleanedLinks,
            });
            toast.success('Campaign Contest updated successfully!');
            navigate(`/campaigns/${formData.campaignID}`);
        } catch (err) {
            setError('Error updating campaign contest');
            toast.error('Error updating campaign contest: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!currentUser?.isAdmin) {
        return <div className="error">Admin access required.</div>;
    }

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error && !formData.name) {
        return <div className="error">{error}</div>;
    }

    return (
        <div className="admin-page">
            <div className="banner-header">
                <BackButton to={`/campaigns/${formData.campaignID}`} />
                <h2>Edit Contest</h2>
            </div>
            <div className="admin-form admin-form--vertical">
                <div className="admin-field">
                    <label>Campaign</label>
                    <select name="campaignID" value={formData.campaignID} onChange={handleInputChange}>
                        <option value="">Select campaign</option>
                        {[...campaigns].sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                            <option key={c.campaignID} value={c.campaignID}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div className="admin-field">
                    <label>Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} />
                </div>
                <div className="admin-field">
                    <label>Description</label>
                    <textarea name="desc" value={formData.desc} onChange={handleInputChange} />
                </div>
                <div className="admin-form__row">
                    <div className="admin-field">
                        <label>Comp Team</label>
                        <select name="userTeamID" value={formData.userTeamID} onChange={handleInputChange}>
                            <option value="">Select comp team</option>
                            {compTeams.map(team => (
                                <option key={team.userTeamID} value={team.userTeamID}>
                                    {team.name} ({team.score || 0})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="admin-field">
                        <label>Number of Games</label>
                        <input type="number" name="numOfGames" value={formData.numOfGames} onChange={handleInputChange} />
                    </div>
                </div>
                <fieldset className="admin-fieldset">
                    <legend>Reward</legend>
                    <div className="admin-field">
                        <label>Reward</label>
                        <select name="rewardID" value={formData.rewardID} onChange={handleInputChange}>
                            <option value="">No reward</option>
                            {rewards.map(r => (
                                <option key={r.rewardID} value={r.rewardID}>
                                    {formatRewardLabel(r)}
                                </option>
                            ))}
                        </select>
                        <a href="/rewards/create" target="_blank" rel="noopener noreferrer" className="admin-field__helper-link">+ Create New Reward</a>
                    </div>
                    <div className="admin-field">
                        <label>Max Rewards</label>
                        <input type="number" name="maxRewards" min="1" value={formData.maxRewards} onChange={handleInputChange} placeholder="Unlimited" />
                    </div>
                </fieldset>
                <ConstraintEditor
                    constraints={formData.constraints}
                    onChange={newConstraints => setFormData(prev => ({ ...prev, constraints: newConstraints }))}
                    constraintTypeDefs={constraintTypeDefs}
                />
                <fieldset className="admin-fieldset">
                    <legend>Links</legend>
                    {formData.links.map((link, i) => (
                        <div key={i} className="admin-form__row">
                            <div className="admin-field">
                                <label>Label</label>
                                <input type="text" placeholder="Label" value={link.label} onChange={e => handleLinkChange(i, 'label', e.target.value)} />
                            </div>
                            <div className="admin-field">
                                <label>URL</label>
                                <input type="text" placeholder="https://..." value={link.url} onChange={e => handleLinkChange(i, 'url', e.target.value)} />
                            </div>
                            <button type="button" onClick={() => handleRemoveLink(i)} style={{ alignSelf: 'flex-end' }}>Remove</button>
                        </div>
                    ))}
                    <button type="button" onClick={handleAddLink}>+ Add Link</button>
                </fieldset>
                <div className="actions-cell">
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => navigate(`/campaigns/${formData.campaignID}`)}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

export default EditCampaignContestPage;
